/**
 * Unsubscribe Page
 *
 * Shows confirmation after unsubscribing from emails.
 * Also allows manual unsubscribe by entering email.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success', 'error', 'already'
  const [message, setMessage] = useState('');

  // Check if coming from email link
  useEffect(() => {
    const success = searchParams.get('success');
    const emailParam = searchParams.get('email');

    if (success === 'true' && emailParam) {
      setEmail(emailParam);
      setStatus('success');
      setMessage('You have been successfully unsubscribed from our emails.');
    }
  }, [searchParams]);

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setStatus(null);

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'unsubscribe', email })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage('You have been successfully unsubscribed from our emails.');
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    if (!email) return;

    setLoading(true);

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'resubscribe' })
      });

      if (response.ok) {
        setStatus('resubscribed');
        setMessage('Welcome back! You have been resubscribed to our emails.');
      }
    } catch (error) {
      console.error('Resubscribe error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] dark:bg-[#1d1d1f] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#2c2c2e] rounded-3xl shadow-xl max-w-md w-full p-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="/logo-blue.png"
              alt="QuickMeet"
              className="h-12 mx-auto mb-4"
            />
          </Link>
        </div>

        {status === 'success' || status === 'resubscribed' ? (
          <div className="text-center">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
              status === 'resubscribed' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
            }`}>
              <svg className={`w-8 h-8 ${status === 'resubscribed' ? 'text-green-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#1d1d1f] dark:text-white mb-2">
              {status === 'resubscribed' ? 'Welcome Back!' : 'Unsubscribed'}
            </h1>
            <p className="text-[#86868b] mb-6">{message}</p>

            {status === 'success' && (
              <div className="space-y-4">
                <p className="text-sm text-[#86868b]">
                  Changed your mind?
                </p>
                <button
                  onClick={handleResubscribe}
                  disabled={loading}
                  className="w-full py-3 bg-[#06c] text-white rounded-xl font-medium hover:bg-[#0055d4] transition-colors disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Resubscribe'}
                </button>
              </div>
            )}

            <Link
              to="/"
              className="inline-block mt-6 text-[#06c] hover:underline"
            >
              Return to QuickMeet
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-[#1d1d1f] dark:text-white mb-2">
                Unsubscribe
              </h1>
              <p className="text-[#86868b]">
                Enter your email to unsubscribe from our mailing list.
              </p>
            </div>

            {status === 'error' && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <p className="text-red-600 dark:text-red-400 text-sm">{message}</p>
              </div>
            )}

            <form onSubmit={handleUnsubscribe} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#06c]"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-3 bg-[#1d1d1f] dark:bg-white text-white dark:text-[#1d1d1f] rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Unsubscribe'}
              </button>
            </form>

            <p className="text-center text-sm text-[#86868b] mt-6">
              You can also manage your email preferences in your{' '}
              <Link to="/login" className="text-[#06c] hover:underline">
                account settings
              </Link>
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
}
